import _includes from 'lodash/includes';
import {PERSON_PAGE_TYPE, FUNERAL_HOME_TYPE} from './constants';
import {overrideMenuOptions} from './menuHelpers';

const funeralHomePageTypes = [
  PERSON_PAGE_TYPE.batesvillePersonPage,
  PERSON_PAGE_TYPE.forestLawnPersonPage,
  PERSON_PAGE_TYPE.tributesFhPersonPage,
];

/** Get the obit source id required for a valid FH obit request
 * @param   {object} query person page query params
 * @returns {string} returns the obit source id for that view
 */
export function getQueryObitSourceId(query) {
  switch (query.v) {
    case FUNERAL_HOME_TYPE.forestlawn:
    case FUNERAL_HOME_TYPE.tributes:
      return query.sid;
    case FUNERAL_HOME_TYPE.batesville:
      return query.pid;
    default:
      return 0;
  }
}

/** Get the obit source (if not legacy) based on the requested view
 * @param   {string} view person page view
 * @returns {string} returns the obit source string
 */
export function getViewObitSource(view) {
  if (view === FUNERAL_HOME_TYPE.forestlawn) {
    return FUNERAL_HOME_TYPE.tributes;
  }
  return view;
}

/** Get the css class specific to the view
 * @param   {string} view person page view
 * @returns {string} returns the global css class
 */
export function getViewClass(view) {
  if (view) {
    return `${view}View`;
  }
  return null;
}

/** Translate the requested view into a Person Page Type key
 * @param   {string} view person page view
 * @returns {string} returns the person page type key
 */
export function getViewPageType(view) {
  const pageTypes = {
    tributes: PERSON_PAGE_TYPE.tributesFhPersonPage,
    forestlawn: PERSON_PAGE_TYPE.forestLawnPersonPage,
    batesville: PERSON_PAGE_TYPE.batesvillePersonPage,
    default: PERSON_PAGE_TYPE.personPage,
  };

  return pageTypes[view] || pageTypes.default;
}

/** Determine if the given person page type key is for a funeral home person page
 * @param   {string}    pageType person page type key
 * @returns {boolean} returns indicator of person page supporting a funeral home
 */
export function isFuneralHomePageType(pageType) {
  return _includes(funeralHomePageTypes, pageType);
}

/** Determine if the given person page type key is for a Batesville person page
 * @param   {string}    pageType person page type key
 * @returns {boolean} returns indicator of person page being a Batesville type
 */
export function isBatesvillePageType(pageType) {
  return pageType === PERSON_PAGE_TYPE.batesvillePersonPage;
}

/** Determine if the given person page type key is for a Forest Lawn person page
 * @param   {string}    pageType person page type key
 * @returns {boolean} returns indicator of person page being a FL type
 */
export function isForestLawnPageType(pageType) {
  return pageType === PERSON_PAGE_TYPE.forestLawnPersonPage;
}

/** Get the background image for the given person page type key
 * @param   {string}    pageType person page type key
 * @returns {string} returns background image filename
 */
export function getPageTypeBackgroundImage(pageType) {
  if (isBatesvillePageType(pageType)) {
    return 'batesville-hero-2x.jpg';
  }
  if (isForestLawnPageType(pageType)) {
    return 'forestlawn-hero-2x.jpg';
  }
  return 'hero-img-2x.jpg';
}

/** Get the background image for the flowers CTA
 * @param   {string}    pageType person page type key
 * @returns {string} returns background image filename
 */
export function getPageTypeFlowersCtaImage(pageType) {
  if (isForestLawnPageType(pageType)) {
    return 'forestlawn-flowers.png';
  }
  return 'flowers.png';
}

/** Get the layout key for the given person page type key
 * @param   {string}    pageType person page type key
 * @param   {string}    timeBasedLayoutType layout type based on time
 * @returns {string} returns 'obit' or 'guestbook' layout indicator
 */
export function getPageTypeLayoutKey(pageType, timeBasedLayoutType) {
  if (isBatesvillePageType(pageType)) {
    return 'guestbook';
  }
  return timeBasedLayoutType;
}

/**
 * Returns layout map based on person page type and layout type
 * @param   {string}  pageType person page type key
 * @param  {string}   layoutKey string representation of layout type
 * @return {object} layout map
 */
export function getPageTypeLayoutMap(pageType, layoutKey) {
  let layouts = null;

  switch (pageType) {
    case PERSON_PAGE_TYPE.forestLawnPersonPage:
    case PERSON_PAGE_TYPE.tributesFhPersonPage:
      layouts = {
        guestbook: {
          dual: {
            left: ['guestbook'],
            right: [
              'obituary',
              'gallery',
              'mainService',
              'serviceInfo',
              'resourcesBlock',
            ],
          },
          single: [
            'obituary',
            'gallery',
            'guestbook',
            'mainService',
            'serviceInfo',
            'resourcesBlock',
          ],
        },
        obit: {
          dual: {
            left: ['obituary', 'guestbook'],
            right: [
              'mainService',
              'flowers',
              'serviceInfo',
              'resourcesBlock',
              'gallery',
            ],
          },
          single: [
            'obituary',
            'mainService',
            'serviceInfo',
            'gallery',
            'guestbook',
            'resourcesBlock',
          ],
        },
      };
      break;
    case PERSON_PAGE_TYPE.batesvillePersonPage:
      layouts = {
        guestbook: {
          dual: {
            left: ['guestbook', 'alerts'],
            right: [
              'mainService',
              'flowers',
              'obituary',
              'gallery',
              'resourcesBlock',
            ],
          },
          single: [
            'obituary',
            'gallery',
            'guestbook',
            'alerts',
            'mainService',
            'resourcesBlock',
          ],
        },
      };
      break;
    default:
      layouts = {
        guestbook: {
          dual: {
            left: ['guestbook', 'alerts'],
            right: [
              'obituary',
              'gallery',
              'resourcesBlock',
              'mainService',
              'serviceInfo',
            ],
          },
          single: [
            'obituary',
            'gallery',
            'guestbook',
            'mainService',
            'alerts',
            'serviceInfo',
            'resourcesBlock',
          ],
        },
        obit: {
          dual: {
            left: ['obituary', 'guestbook', 'alerts'],
            right: [
              'mainService',
              'flowers',
              'serviceInfo',
              'resourcesBlock',
              'gallery',
            ],
          },
          single: [
            'obituary',
            'mainService',
            'serviceInfo',
            'gallery',
            'guestbook',
            'alerts',
            'resourcesBlock',
          ],
        },
      };
      break;
  }

  return layouts[layoutKey.toLowerCase()];
}

/** Get the analyticPageCategory for the given person page type key
 * @param   {string}    pageType person page type key
 * @param   {string}    columnView column view key
 * @returns {string} returns analyticPageCategory for given page type and column view
 */
export function getAnalyticPageCategory(pageType, columnView) {
  if (isBatesvillePageType(pageType)) {
    return 'fh_gb';
  }
  if (isFuneralHomePageType(pageType)) {
    return `fh_obit_${columnView}`;
  }
  return `person_page_${columnView}`;
}

/** Does the person page type use Legacy Menus
 * @param   {string}    pageType person page type key
 * @returns {boolean} returns whether to use Legacy menus
 */
export function useLegacyMenus(pageType) {
  return !isFuneralHomePageType(pageType) || isBatesvillePageType(pageType);
}

/** Tailors the menu header per obit/page type
 * @param   {Object}   headerData input header menu
 * @param   {string}   pageType person page type key
 * @param   {boolean}  disableFlowers obit flowers override switch
 * @returns {string} returns updated header menu
 */
export function tailorMenu(headerData, pageType, disableFlowers) {
  let headerStore = overrideMenuOptions(
    headerData,
    'type',
    'flowers',
    disableFlowers
  );

  headerStore = overrideMenuOptions(
    headerStore,
    'name',
    'Send Flowers',
    isBatesvillePageType(pageType)
  );
  headerStore = overrideMenuOptions(
    headerStore,
    'name',
    'Funeral Homes',
    isBatesvillePageType(pageType)
  );
  return headerStore;
}

/** Determines whether printed gb ad is viable for the person page type
 * @param   {string}    pageType person page type key
 * @returns {boolean} returns whether to use Printed GB Ad
 */
export function allowPrintedGuestBookAd(pageType) {
  return !isForestLawnPageType(pageType);
}

/** Determines whether to emphasize buttons through a pure button layout
 * @param   {string}    pageType person page type key
 * @returns {boolean} returns whether to emphasize Read Obit CTA
 */
export function useButtonsWithoutDropdown(pageType) {
  return isBatesvillePageType(pageType);
}



// WEBPACK FOOTER //
// ./src/reactModules/shared/utilities/personPageType.js